// @ts-nocheck
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { formatContentDuration } from "@/lib/utils";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CardSkeleton, TableSkeleton } from "@/components/loading";
import { toast } from "sonner";
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Clock,
  GraduationCap,
  Users,
  UserPlus,
  TrendingUp,
  CheckCircle,
  XCircle,
  ArrowUp,
  ArrowDown,
  X,
  ChevronRight,
  Lock,
  Pencil,
  Award,
  FolderKanban,
  Tag,
  GripVertical,
  Search,
} from "lucide-react";
import {
  fetchLearningTracks,
  createLearningTrack,
  updateLearningTrack,
  deleteLearningTrack,
  fetchTrackCourses,
  addCourseToTrack,
  removeCourseFromTrack,
  reorderTrackCourse,
} from "@/lib/api/enrollment";
import { Switch } from "@/components/ui/switch";

import {
  fetchAllCourses,
  fetchCoursesPaged,
  updateCourse,
  deleteCourse,
  deleteCourseEnrollment,
  createCourseEnrollments,
} from "@/lib/api/courses";
import { fetchLearningTracksPaged } from "@/lib/api/enrollment";
import { PaginationControls } from "@/components/PaginationControls";
import { Combobox } from "@/components/ui/combobox";
import { useAuth } from "@/hooks/use-auth";
import { canManageCourse, canManageTrack } from "@/lib/rbac";
import { fetchFinalExamOptions } from "@/lib/api/exams";

export const Route = createFileRoute("/_authenticated/admin/courses")({
  component: () => (
    <RequirePermission
      action="manage:courses"
      fallback={
        <AccessDenied message="Course authoring is limited to Content Creators and System Admins." />
      }
    >
      <CoursesAdminPage />
    </RequirePermission>
  ),
});

import type { Course, TrainingModule, CourseModule } from "@/lib/types";

type CourseWithModules = Course & {
  modules: (CourseModule & { module: TrainingModule })[];
  enrollment_count?: number;
};

function useDebouncedValue<T>(value: T, ms = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function CoursesAdminPage() {
  const { hasPermission, isAdmin, user } = useAuth();
  // Ownership affordances. A ContentCreator can now SEE every course and track on the platform,
  // so most rows below belong to a colleague; only their author (or an admin) may modify them.
  // The server enforces this — PUT/DELETE answer 403 — and these helpers mirror it so the UI
  // never offers an action that is going to be refused.
  const caller = { isAdmin, userId: user?.id };
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPublished, setFilterPublished] = useState<"all" | "published" | "draft">("all");
  const [coursePage, setCoursePage] = useState(1);
  const [coursePageSize, setCoursePageSize] = useState(10);
  const debouncedCourseSearch = useDebouncedValue(searchQuery);

  const [viewingCourseId, setViewingCourseId] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    setCoursePage(1);
  }, [debouncedCourseSearch, filterPublished]);

  const courseStatusParam = filterPublished === "all" ? undefined : (filterPublished === "published" ? "published" : "unpublished");

  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ["admin-courses", "paged", coursePage, coursePageSize, debouncedCourseSearch, courseStatusParam],
    queryFn: () =>
      fetchCoursesPaged({
        pageNumber: coursePage,
        pageSize: coursePageSize,
        search: debouncedCourseSearch || undefined,
        status: courseStatusParam,
      }),
  });

  const courses = coursesData?.items ?? [];
  const isLoading = coursesLoading;

  const { mutate: delCourse, isPending: isDeleting } = useMutation({
    mutationFn: async (courseId: string) => {
      await deleteCourse(courseId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-courses"] });
      toast.success("Course deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete course: ${error.message}`);
    },
  });

  const togglePublished = useMutation({
    mutationFn: async (payload: {
      id: string;
      title: string;
      description?: string | null;
      is_published: boolean;
    }) => {
      await updateCourse(payload.id, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-courses"] });
      toast.success("Course visibility updated");
    },
  });

  // Removed client-side filteredCourses since courses are filtered server-side.

  const goToEditor = (id: string) =>
    navigate({ to: "/admin/courses/$courseId", params: { courseId: id } });

  // ─── Categories States & Logic ──────────────────────────────────
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any | null>(null);
  const [categoryNameInput, setCategoryNameInput] = useState("");
  const [categoryIsActiveInput, setCategoryIsActiveInput] = useState(true);

  const { data: categories = [], isLoading: isCategoriesLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      return [] as { id: string; name: string }[]; // Replace with real API fetch if available
    },
  });

  const createCategory = useMutation({
    mutationFn: async (name: string) => {
      // Mock API logic
      console.log("create category", name);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Category created");
    },
  });

  const updateCategory = useMutation({
    mutationFn: async (payload: any) => {
      // Mock API logic
      console.log("update category", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Category updated");
    },
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      // Mock API logic
      console.log("delete category", id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Category deleted");
    },
  });

  // ─── Learning Tracks States & Logic ──────────────────────────────────
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [trackSearchQuery, setTrackSearchQuery] = useState("");
  const [courseComboboxOpen, setCourseComboboxOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Track dialog state
  const [trackDialogOpen, setTrackDialogOpen] = useState(false);
  const [editingTrack, setEditingTrack] = useState<any | null>(null);
  const [nameInput, setNameInput] = useState("");
  const [descInput, setDescInput] = useState("");
  const [isActiveInput, setIsActiveInput] = useState(true);
  const [finalExamIdInput, setFinalExamIdInput] = useState<string | null>(null);
  const [issuesCertInput, setIssuesCertInput] = useState(true);

  const [trackPage, setTrackPage] = useState(1);
  const [trackPageSize, setTrackPageSize] = useState(10);
  const debouncedTrackSearch = useDebouncedValue(trackSearchQuery);

  // Reset page when search changes
  useEffect(() => {
    setTrackPage(1);
  }, [debouncedTrackSearch]);

  // Queries
  const { data: tracksData, isLoading: isTracksLoading } = useQuery({
    queryKey: ["learning_tracks", "paged", trackPage, trackPageSize, debouncedTrackSearch],
    queryFn: () =>
      fetchLearningTracksPaged({
        pageNumber: trackPage,
        pageSize: trackPageSize,
        search: debouncedTrackSearch || undefined,
      }),
  });

  const tracks = tracksData?.items ?? [];

  const { data: trackCourses = [], isLoading: isCoursesLoading } = useQuery({
    queryKey: ["track_courses", selectedTrackId],
    queryFn: () => fetchTrackCourses(selectedTrackId || ""),
    enabled: !!selectedTrackId,
  });

  const { data: allCourses = [] } = useQuery({
    queryKey: ["all_published_courses"],
    queryFn: async () => {
      return await fetchAllCourses();
    },
  });

  // Track final-exam picker. Only TrackFinal: the backend's
  // TrackService.ValidateTrackFinalExamAsync requires an exact type match, so listing Assignment
  // exams here (as this used to) offered options whose save was guaranteed to fail with
  // "must be of type Track Final".
  const [trackExamSearch, setTrackExamSearch] = useState("");
  const { data: trackExamResult, isLoading: trackExamsLoading, isError: trackExamError } = useQuery({
    queryKey: ["track-final-exams", trackExamSearch],
    queryFn: () =>
      fetchFinalExamOptions({
        kind: "TrackFinal",
        search: trackExamSearch,
      }),
    enabled: trackDialogOpen,
    retry: false,
  });
  // Inject the currently-linked exam so it stays visible/selected even if the search excludes it
  // (mirrors course-checkpoints.tsx:476-483).
  const trackExams = useMemo(() => {
    const rows = trackExamResult?.items ?? [];
    const opts = rows.map((e: any) => ({
      value: e.id,
      label: e.title,
      hint: `${e.exam_type} · ${e.time_limit_minutes} min · pass ${e.passing_score}%${!e.is_active ? " · Draft" : ""}`,
    }));
    if (finalExamIdInput && !opts.some((o: any) => o.value === finalExamIdInput)) {
      // The linked exam is not in the search results — inject it so saving doesn't clear a working link.
      const linked = (trackExamResult?.items ?? []).find((e: any) => e.id === finalExamIdInput);
      opts.unshift({
        value: finalExamIdInput,
        label: linked?.title ?? "Current final exam",
        hint: linked ? `${linked.exam_type} · ${linked.time_limit_minutes} min · pass ${linked.passing_score}%${!linked.is_active ? " · Draft" : ""}` : "",
      });
    }
    return opts;
  }, [trackExamResult, finalExamIdInput]);

  // Memoized values
  const selectedTrack = useMemo(() => {
    if (!Array.isArray(tracks)) return undefined;
    return tracks.find((t) => (t.id || t.Id) === selectedTrackId);
  }, [tracks, selectedTrackId]);

  const availableCourses = useMemo(() => {
    const assignedIds = new Set(trackCourses.map((tc) => tc.course_id));
    return allCourses.filter((c) => !assignedIds.has(c.id));
  }, [allCourses, trackCourses]);

  // Removed client-side filteredTracks since tracks are filtered server-side.

  // Mutations
  const upsertTrack = useMutation({
    mutationFn: async () => {
      if (!nameInput.trim()) throw new Error("Track name is required.");
      if (editingTrack) {
        return await updateLearningTrack(editingTrack.id, {
          name: nameInput.trim(),
          description: descInput.trim() || null,
          issues_certificate: issuesCertInput,
          final_exam_id: finalExamIdInput,
        });
      } else {
        return await createLearningTrack({
          name: nameInput.trim(),
          description: descInput.trim() || null,
          issues_certificate: issuesCertInput,
          final_exam_id: finalExamIdInput,
        } as any);
      }
    },
    onSuccess: async (createdResult: any) => {
      await qc.invalidateQueries({ queryKey: ["learning_tracks"] });
      const createdId = createdResult?.id || createdResult?.trackId;
      if (!editingTrack && createdId) {
        setSelectedTrackId(createdId);
      }
      toast.success(editingTrack ? "Learning track updated" : "Learning track created");
      setTrackDialogOpen(false);
    },
    onError: (e: any) => {
      toast.error(e?.message || "Failed to save learning track");
    },
  });

  const removeTrack = useMutation({
    mutationFn: async (id: string) => {
      await deleteLearningTrack(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["learning_tracks"] });
      if (selectedTrackId === editingTrack?.id) {
        setSelectedTrackId(null);
      }
      toast.success("Learning track deleted");
    },
    onError: (e: any) => {
      toast.error(
        e?.message ||
          "Failed to delete learning track. Make sure it has no active enrollments or courses.",
      );
    },
  });

  const addCourse = useMutation({
    mutationFn: async (courseId: string) => {
      if (!selectedTrackId) return;
      const nextSeq =
        trackCourses.length > 0 ? Math.max(...trackCourses.map((tc) => tc.sequence_order)) + 1 : 1;
      await addCourseToTrack(selectedTrackId, courseId, nextSeq);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["track_courses", selectedTrackId] });
      toast.success("Course added to track");
    },
    onError: (e: any) => {
      toast.error(e?.message || "Failed to add course to track");
    },
  });

  const removeCourse = useMutation({
    mutationFn: async (courseId: string) => {
      if (!selectedTrackId) return;
      await removeCourseFromTrack(selectedTrackId, courseId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["track_courses", selectedTrackId] });
      toast.success("Course removed from track");
    },
    onError: (e: any) => {
      toast.error(e?.message || "Failed to remove course from track");
    },
  });

  const reorderCourses = useMutation({
    mutationFn: async ({ index, direction }: { index: number; direction: "up" | "down" }) => {
      if (!selectedTrackId) return;
      const siblingIndex = direction === "up" ? index - 1 : index + 1;
      if (siblingIndex < 0 || siblingIndex >= trackCourses.length) return;

      const current = trackCourses[index];
      const sibling = trackCourses[siblingIndex];

      const currentSeq = current.sequenceOrder ?? current.sequence_order ?? index + 1;
      const siblingSeq = sibling.sequenceOrder ?? sibling.sequence_order ?? siblingIndex + 1;

      const currentCourseId = current.course_id || current.courseId;
      const siblingCourseId = sibling.course_id || sibling.courseId;

      if (!currentCourseId || !siblingCourseId) return;

      // Update both
      await reorderTrackCourse(selectedTrackId, currentCourseId, siblingSeq);
      await reorderTrackCourse(selectedTrackId, siblingCourseId, currentSeq);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["track_courses", selectedTrackId] });
      toast.success("Courses reordered");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to reorder courses");
    },
  });

  // Dialog Handlers
  const openAddTrack = () => {
    setEditingTrack(null);
    setNameInput("");
    setDescInput("");
    setIsActiveInput(true);
    setFinalExamIdInput(null);
    setIssuesCertInput(true);
    setTrackDialogOpen(true);
  };

  const openEditTrack = (track: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTrack(track);
    setNameInput(track.name);
    setDescInput(track.description || "");
    setIsActiveInput(track.is_active);
    setFinalExamIdInput(track.final_exam_id ?? track.finalExamId ?? null);
    setIssuesCertInput(
      track.hasCertificate ?? track.HasCertificate ?? track.issues_certificate ?? true,
    );
    setTrackDialogOpen(true);
  };

  const handleDeleteTrack = (track: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(`Are you sure you want to delete "${track.name}"? This cannot be undone.`)) return;
    removeTrack.mutate(track.id);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-4 border-b border-border/40">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-xl">
              <GraduationCap className="h-6 w-6 text-primary" />
            </div>
            Training Configuration
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Manage courses, final exams, and learning tracks sequencing.
          </p>
        </div>
      </div>

      <Tabs defaultValue="courses" className="space-y-6">
        <TabsList className="inline-flex h-10 items-center justify-center rounded-full bg-muted/50 p-1 text-muted-foreground">
          <TabsTrigger
            value="courses"
            className="rounded-full px-6 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm transition-all"
          >
            Courses
          </TabsTrigger>
          <TabsTrigger
            value="tracks"
            className="rounded-full px-6 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm transition-all"
          >
            Learning Tracks
          </TabsTrigger>
        </TabsList>

        <TabsContent value="courses" className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Course Management</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Create and manage training courses with final exams
              </p>
            </div>
            {hasPermission("manage:courses") && (
              <Button onClick={() => goToEditor("new")} className="shadow-sm rounded-full px-6">
                <Plus className="h-4 w-4 mr-2" />
                Create Course
              </Button>
            )}
          </div>

          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <Input
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-md"
              />
            </div>
            <Select value={filterPublished} onValueChange={(v: any) => setFilterPublished(v)}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="draft">Drafts</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <CardSkeleton count={6} className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" />
          ) : courses.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">
                  {searchQuery || filterPublished !== "all"
                    ? "No courses found matching your filters"
                    : "No courses created yet. Create your first course to get started."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {courses.map((course) => (
                <div
                  key={course.id}
                  className="group relative flex flex-col rounded-xl border border-border/60 bg-card shadow-sm hover:shadow-lg hover:border-primary/30 transition-all duration-300 cursor-pointer overflow-hidden"
                  onClick={() => goToEditor(course.id)}
                >
                  {/* Top accent bar */}
                  <div
                    className={`h-1 w-full flex-shrink-0 ${
                      course.is_published
                        ? "bg-gradient-to-r from-emerald-400 to-teal-500"
                        : "bg-gradient-to-r from-zinc-300 to-zinc-400"
                    }`}
                  />

                  <div className="flex flex-col flex-1 p-5 gap-4">
                    {/* Header row */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold tracking-tight text-foreground leading-snug line-clamp-2">
                          {course.title}
                        </h3>
                        <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                          {course.description || "No description provided."}
                        </p>
                        {course.created_by_name && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            by <span className="font-medium">{course.created_by_name}</span>
                          </p>
                        )}
                      </div>

                      {/* Publish toggle */}
                      <button
                        className={`flex-shrink-0 mt-0.5 h-7 w-7 rounded-full flex items-center justify-center transition-colors ${
                          course.is_published
                            ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                            : "bg-muted text-muted-foreground hover:bg-muted/70"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePublished.mutate({
                            id: course.id,
                            title: course.title,
                            description: course.description,
                            is_published: !course.is_published,
                          });
                        }}
                        title={course.is_published ? "Unpublish course" : "Publish course"}
                      >
                        {course.is_published ? (
                          <Eye className="h-3.5 w-3.5" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>

                    {/* Stats pills */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/60 text-xs font-medium text-muted-foreground">
                        <BookOpen className="h-3 w-3" />
                        {(course as any).module_count ?? course.modules?.length ?? 0} modules
                      </span>
                      {course.total_duration_seconds > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/60 text-xs font-medium text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {formatContentDuration(course.total_duration_seconds)}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/60 text-xs font-medium text-muted-foreground">
                        <Users className="h-3 w-3" />
                        {course.enrollment_count ?? 0}
                      </span>
                    </div>

                    {/* Footer: badges + actions */}
                    <div className="flex items-center justify-between gap-2 pt-3 border-t border-border/50 mt-auto">
                      {/* Badges */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {course.is_published ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />
                            Published
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-zinc-100 text-zinc-500 border border-zinc-200">
                            <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 inline-block" />
                            Draft
                          </span>
                        )}
                        {course.final_exam_id && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                            Exam
                          </span>
                        )}
                        {(course as any).issues_certificate === false && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-zinc-50 text-zinc-500 border border-zinc-200">
                            No Cert
                          </span>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div
                        className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canManageCourse(caller, course) ? (
                          <>
                            <button
                              className="h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                              onClick={() => goToEditor(course.id)}
                              title="Edit course"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </button>
                            <button
                              className="h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                              onClick={() => {
                                if (
                                  confirm(`Delete course "${course.title}"? This cannot be undone.`)
                                ) {
                                  delCourse(course.id);
                                }
                              }}
                              title="Delete course"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        ) : (
                          <button
                            className="h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            onClick={() => goToEditor(course.id)}
                            title="View course (authored by another user)"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4">
            <PaginationControls
              pageNumber={coursePage}
              pageSize={coursePageSize}
              totalPages={coursesData?.totalPages ?? 1}
              totalCount={coursesData?.totalCount ?? 0}
              hasNextPage={coursesData?.hasNextPage ?? false}
              hasPreviousPage={coursesData?.hasPreviousPage ?? false}
              onPageChange={setCoursePage}
              onPageSizeChange={setCoursePageSize}
              isLoading={coursesLoading}
            />
          </div>
        </TabsContent>

        <TabsContent value="tracks" className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Learning Tracks</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Define structured training pathways consisting of multiple courses in sequence.
              </p>
            </div>
            <Button onClick={openAddTrack} className="shadow-sm rounded-full px-6">
              <Plus className="h-4 w-4 mr-2" />
              Create Track
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left Side: Track List */}
            <Card className="md:col-span-1 flex flex-col min-h-[400px] border-border/50 shadow-sm">
              <CardHeader className="pb-4 border-b border-border/30">
                <CardTitle className="text-lg">Tracks</CardTitle>
                <CardDescription>Select a track to manage its courses.</CardDescription>
              </CardHeader>
              <div className="p-3 border-b border-border/30 bg-muted/20">
                <div className="relative">
                  <Input
                    placeholder="Search tracks..."
                    value={trackSearchQuery}
                    onChange={(e) => setTrackSearchQuery(e.target.value)}
                    className="h-9 pl-9 bg-background shadow-sm"
                  />
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                </div>
              </div>
              <CardContent className="p-2 flex-1 overflow-auto bg-muted/10">
                {isTracksLoading ? (
                  <TableSkeleton rows={5} columns={1} className="border-none" />
                ) : tracks.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground text-sm">
                    No tracks found.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {tracks.map((track, idx) => {
                      const tId = track.id || track.Id;
                      const isSelected = tId === selectedTrackId;
                      return (
                        <div
                          key={tId || `track-${idx}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedTrackId(tId);
                          }}
                          className={`group flex items-center justify-between p-3 rounded-md cursor-pointer transition-all duration-200 border-l-4 ${
                            isSelected
                              ? "bg-primary/5 border-l-primary shadow-sm"
                              : "bg-transparent border-l-transparent hover:bg-accent/50 hover:border-l-border"
                          }`}
                        >
                          <div className="min-w-0 flex-1 pr-2">
                            <div className="flex items-center gap-2">
                              <p
                                className={`truncate text-sm ${isSelected ? "font-semibold text-foreground" : "font-medium text-foreground/80 group-hover:text-foreground"}`}
                              >
                                {track.name}
                              </p>
                            </div>
                            {track.description && (
                              <p className="text-xs text-muted-foreground truncate mt-1 opacity-80 group-hover:opacity-100 transition-opacity">
                                {track.description}
                              </p>
                            )}
                            {/* Summed by the API from this track's courses, each of which is in
                                turn the sum of its modules. These come straight off the API with
                                no adapter, so the field is camelCase. */}
                            {track.totalDurationSeconds > 0 && (
                              <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                {formatContentDuration(track.totalDurationSeconds)}
                              </p>
                            )}
                          </div>
                          <div
                            className={`flex items-center gap-0.5 shrink-0 transition-opacity ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                          >
                            {canManageTrack(caller, track) ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                  onClick={(e) => openEditTrack(track, e)}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                  onClick={(e) => handleDeleteTrack(track, e)}
                                  disabled={removeTrack.isPending}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            ) : (
                              <Lock
                                className="h-3.5 w-3.5 text-muted-foreground/60"
                                title="View only — authored by another user"
                                aria-label="View only"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
              <div className="p-3 border-t border-border/30 bg-muted/20">
                <PaginationControls
                  pageNumber={trackPage}
                  pageSize={trackPageSize}
                  totalPages={tracksData?.totalPages ?? 1}
                  totalCount={tracksData?.totalCount ?? 0}
                  hasNextPage={tracksData?.hasNextPage ?? false}
                  hasPreviousPage={tracksData?.hasPreviousPage ?? false}
                  onPageChange={setTrackPage}
                  onPageSizeChange={setTrackPageSize}
                  isLoading={isTracksLoading}
                  showDetails={false}
                />
              </div>
            </Card>

            {/* Right Side: Track Course Sequence Management */}
            <Card className="md:col-span-2 border-border/50 shadow-sm flex flex-col min-h-[400px]">
              <CardHeader className="pb-4 border-b border-border/30 bg-card">
                <CardTitle className="text-lg">
                  {selectedTrack ? `Manage: ${selectedTrack.name}` : "Course Sequence"}
                </CardTitle>
                <CardDescription>
                  {selectedTrack
                    ? "Add, remove, or drag/reorder courses that students must complete in sequence."
                    : "Select a track from the list to manage its course sequence."}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 flex-1 bg-muted/5">
                {selectedTrack ? (
                  <div className="space-y-6">
                    {/* Add Course Select */}
                    {availableCourses.length > 0 ? (
                      <div className="flex gap-2">
                        <Popover open={courseComboboxOpen} onOpenChange={setCourseComboboxOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-between font-normal bg-background shadow-sm hover:bg-accent/50"
                            >
                              <span className="text-muted-foreground">
                                Select a course to add to this track...
                              </span>
                              <ChevronRight className="h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[400px] p-0" align="start">
                            <Command>
                              <CommandInput placeholder="Search course..." />
                              <CommandList>
                                <CommandEmpty>No courses found.</CommandEmpty>
                                <CommandGroup>
                                  {availableCourses.map((c) => (
                                    <CommandItem
                                      key={c.id}
                                      value={c.title}
                                      onSelect={() => {
                                        addCourse.mutate(c.id);
                                        setCourseComboboxOpen(false);
                                      }}
                                    >
                                      {c.title}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>
                    ) : allCourses.length > 0 ? (
                      <div className="bg-primary/5 border border-primary/20 rounded-md p-3 flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-primary" />
                        <p className="text-sm font-medium text-primary">
                          All published courses have been assigned to this track.
                        </p>
                      </div>
                    ) : (
                      <div className="bg-muted/50 border border-border/50 rounded-md p-3 flex items-center gap-3">
                        <BookOpen className="h-5 w-5 text-muted-foreground" />
                        <p className="text-sm font-medium text-muted-foreground">
                          No published courses available. Create and publish courses first.
                        </p>
                      </div>
                    )}

                    {/* Course List */}
                    {isCoursesLoading ? (
                      <TableSkeleton rows={4} columns={1} className="border-none" />
                    ) : trackCourses.length === 0 ? (
                      <div className="py-16 text-center border-2 border-dashed border-border/60 rounded-xl bg-background/50">
                        <div className="bg-primary/5 h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FolderKanban className="h-6 w-6 text-primary/60" />
                        </div>
                        <p className="text-base font-semibold text-foreground">
                          No courses in this track
                        </p>
                        <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                          Add courses from the dropdown above to define the pathway sequence for
                          students.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {trackCourses.map((tc, idx) => (
                          <div
                            key={tc.id}
                            draggable
                            onDragStart={(e) => {
                              setDraggedIndex(idx);
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = "move";
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              if (draggedIndex === null || draggedIndex === idx) return;
                              const direction = idx < draggedIndex ? "up" : "down";
                              reorderCourses.mutate({ index: draggedIndex, direction });
                              setDraggedIndex(null);
                            }}
                            className={`group flex items-center justify-between p-3 bg-background border rounded-lg shadow-sm hover:shadow-md hover:border-primary/30 transition-all cursor-grab active:cursor-grabbing ${
                              draggedIndex === idx ? "opacity-40 border-primary" : "border-border/60"
                            }`}
                          >
                            <div className="flex items-center gap-4 min-w-0">
                              <div
                                className="text-muted-foreground/40 group-hover:text-foreground transition-colors flex items-center justify-center p-1 rounded hover:bg-muted"
                                title="Drag to reorder course"
                              >
                                <GripVertical className="h-5 w-5" />
                              </div>
                              <span className="flex items-center justify-center font-bold text-xs h-7 w-7 rounded-full bg-primary/10 text-primary shrink-0">
                                {idx + 1}
                              </span>
                              <div className="flex flex-col min-w-0">
                                <span className="text-sm font-semibold truncate text-foreground">
                                  {allCourses.find((c) => c.id === tc.course_id)?.title ||
                                    tc.courses?.title ||
                                    tc.course_id}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-1 shrink-0 opacity-80 group-hover:opacity-100 transition-opacity">
                              <div className="flex bg-muted/50 rounded-md border border-border/50 mr-2 overflow-hidden">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 rounded-none border-r border-border/50 hover:bg-background"
                                  disabled={idx === 0 || reorderCourses.isPending}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    reorderCourses.mutate({ index: idx, direction: "up" });
                                  }}
                                  title="Move Up"
                                >
                                  <ArrowUp className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 rounded-none hover:bg-background"
                                  disabled={
                                    idx === trackCourses.length - 1 || reorderCourses.isPending
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    reorderCourses.mutate({ index: idx, direction: "down" });
                                  }}
                                  title="Move Down"
                                >
                                  <ArrowDown className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive rounded-md"
                                disabled={removeCourse.isPending}
                                onClick={() => {
                                  if (confirm(`Remove this course from track?`)) {
                                    removeCourse.mutate(tc.course_id);
                                  }
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-24 text-center text-muted-foreground flex flex-col items-center">
                    <div className="bg-muted/30 h-16 w-16 rounded-full flex items-center justify-center mb-4">
                      <BookOpen className="h-8 w-8 opacity-40" />
                    </div>
                    <p className="text-base font-medium text-foreground">No track selected</p>
                    <p className="text-sm mt-1">
                      Select a track from the sidebar to view and manage its contents.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Create / Edit Track Dialog */}
          <Dialog open={trackDialogOpen} onOpenChange={setTrackDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingTrack ? "Edit Learning Track" : "Create Learning Track"}
                </DialogTitle>
                <DialogDescription>Name and describe this training pathway.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">Name</label>
                  <Input
                    placeholder="e.g. Foundation Training"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Description (Optional)
                  </label>
                  <Textarea
                    placeholder="Briefly explain the target audience or topics covered..."
                    value={descInput}
                    onChange={(e) => setDescInput(e.target.value)}
                    rows={3}
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5 pr-4">
                    <label className="text-sm font-medium">Issues a Track Certificate</label>
                    <p className="text-xs text-muted-foreground">
                      When on, completing every course in this track issues one comprehensive Track
                      certificate. Students enrolled via the track never receive individual course
                      certificates.
                    </p>
                  </div>
                  <Switch checked={issuesCertInput} onCheckedChange={setIssuesCertInput} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Track Final Exam (Optional)
                  </label>
                  <Combobox
                    value={finalExamIdInput}
                    onChange={setFinalExamIdInput}
                    onSearchChange={setTrackExamSearch}
                    isLoading={trackExamsLoading}
                    placeholder="No final exam linked"
                    searchPlaceholder="Search Track Final exams…"
                    emptyMessage={trackExamError
                      ? "Failed to load Track Final exams. Please try again."
                      : "No Track Final exams exist yet — create one in the Exam Center with type “Track Final Exam”."}
                    options={trackExams}
                  />
                  {trackExamError && (
                    <p className="text-xs text-destructive">Failed to load Track Final exams. Check permissions or refresh.</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Locked until every course in the track is completed. Passing it earns the Track
                    certificate.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setTrackDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={() => upsertTrack.mutate()} disabled={upsertTrack.isPending}>
                  {upsertTrack.isPending ? "Saving..." : "Save Track"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CourseDetailDialog({
  courseId,
  onClose,
}: {
  courseId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [showAssignDialog, setShowAssignDialog] = useState(false);

  const { data: courseData } = useQuery({
    queryKey: ["course-detail", courseId],
    queryFn: async () => {
      if (!courseId) return null;

      const course = await fetchCourseDetails(courseId);

      const enrollments = await fetchCourseEnrollments(courseId);

      // Fetch student profiles directly from the profiles table (readable by admins via RLS).
      // auth.admin.listUsers() requires the service_role key and doesn't work on the client.
      const studentIds = [...new Set((enrollments ?? []).map((e) => e.student_id).filter(Boolean))];

      const profileMap = new Map<string, { full_name: string | null; email: string | null }>();
      if (studentIds.length > 0) {
        const profiles = await fetchProfiles(studentIds);
        (profiles ?? []).forEach((p) =>
          profileMap.set(p.id, { full_name: p.full_name, email: p.email }),
        );
      }

      const enrollmentsWithEmails = (enrollments ?? []).map((enrollment) => {
        const profile = profileMap.get(enrollment.student_id);
        return {
          ...enrollment,
          student: {
            id: enrollment.student_id,
            full_name: profile?.full_name ?? null,
            email: profile?.email ?? null,
          },
        };
      });

      return { course, enrollments: enrollmentsWithEmails };
    },
    enabled: !!courseId,
  });

  const unenrollMut = useMutation({
    mutationFn: async (enrollmentId: string) => {
      await deleteCourseEnrollment(enrollmentId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["course-detail", courseId] });
      qc.invalidateQueries({ queryKey: ["admin-courses"] });
      toast.success("Student unenrolled successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to unenroll: ${error.message}`);
    },
  });

  const { hasPermission } = useAuth();
  const canManageUsers = hasPermission("manage:users");

  if (!courseId) return null;

  const course = courseData?.course;
  const enrollments = courseData?.enrollments ?? [];

  const avgProgress =
    enrollments.length > 0
      ? enrollments.reduce((acc, e) => acc + e.completion_percentage, 0) / enrollments.length
      : 0;

  const completedCount = enrollments.filter((e) => e.status === "completed").length;

  return (
    <>
      <Dialog open={!!courseId} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              {course?.title ?? "Course Details"}
            </DialogTitle>
          </DialogHeader>

          {!course ? (
            <div className="py-12 text-center text-muted-foreground">Loading...</div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Course Overview */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <Users className="h-8 w-8 mx-auto text-primary mb-2" />
                      <div className="text-2xl font-bold">{enrollments.length}</div>
                      <div className="text-xs text-muted-foreground">Enrolled</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <CheckCircle className="h-8 w-8 mx-auto text-green-500 mb-2" />
                      <div className="text-2xl font-bold">{completedCount}</div>
                      <div className="text-xs text-muted-foreground">Completed</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <TrendingUp className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                      <div className="text-2xl font-bold">{avgProgress.toFixed(0)}%</div>
                      <div className="text-xs text-muted-foreground">Avg Progress</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <BookOpen className="h-8 w-8 mx-auto text-amber-500 mb-2" />
                      <div className="text-2xl font-bold">
                        {(course as any).module_count ?? course.modules?.length ?? 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Modules</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Enrolled Students */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Enrolled Students</h3>
                  {canManageUsers && (
                    <Button size="sm" onClick={() => setShowAssignDialog(true)}>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Assign Students
                    </Button>
                  )}
                </div>

                {enrollments.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">No students enrolled yet</p>
                      {canManageUsers && (
                        <Button
                          variant="outline"
                          className="mt-4"
                          onClick={() => setShowAssignDialog(true)}
                        >
                          Assign Students
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {enrollments.map((enrollment: any) => (
                      <Card key={enrollment.id}>
                        <CardContent className="py-4">
                          <div className="flex items-center gap-4">
                            <div
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-primary-foreground"
                              style={{ background: "var(--gradient-brand)" }}
                            >
                              {(enrollment.student?.full_name ?? enrollment.student?.email ?? "?")
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium">
                                  {enrollment.student?.full_name || "No name"}
                                </p>
                                <Badge
                                  variant={
                                    enrollment.status === "completed"
                                      ? "default"
                                      : enrollment.status === "in_progress"
                                        ? "secondary"
                                        : "outline"
                                  }
                                  className="text-xs"
                                >
                                  {enrollment.status}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {enrollment.student?.email}
                              </p>
                              <div className="mt-2 space-y-1">
                                <div className="flex justify-between text-xs text-muted-foreground">
                                  <span>Progress</span>
                                  <span>{enrollment.completion_percentage}%</span>
                                </div>
                                <Progress
                                  value={enrollment.completion_percentage}
                                  className="h-2"
                                />
                              </div>
                              {enrollment.completed_at && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Completed {new Date(enrollment.completed_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            {canManageUsers && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => {
                                  if (
                                    confirm(
                                      `Remove ${enrollment.student?.full_name || enrollment.student?.email} from this course?`,
                                    )
                                  ) {
                                    unenrollStudent.mutate(enrollment.id);
                                  }
                                }}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AssignStudentsDialog
        open={showAssignDialog}
        onClose={() => setShowAssignDialog(false)}
        courseId={courseId}
        enrolledStudentIds={enrollments.map((e) => e.student_id)}
      />
    </>
  );
}

function AssignStudentsDialog({
  open,
  onClose,
  courseId,
  enrolledStudentIds,
}: {
  open: boolean;
  onClose: () => void;
  courseId: string;
  enrolledStudentIds: string[];
}) {
  const qc = useQueryClient();
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);

  // Reset selection whenever dialog opens
  useEffect(() => {
    if (open) setSelectedStudentIds([]);
  }, [open]);

  const { data: students = [] } = useQuery({
    queryKey: ["available-students", courseId],
    queryFn: async () => {
      // email is stored directly in profiles — no auth.admin call needed.
      const profiles = await fetchAllProfiles();
      return profiles ?? [];
    },
    enabled: open,
  });

  const assignStudents = useMutation({
    mutationFn: async () => {
      const enrollments = selectedStudentIds.map((studentId) => ({
        course_id: courseId,
        student_id: studentId,
        status: "enrolled",
        completion_percentage: 0,
      }));

      await createCourseEnrollments(enrollments);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["course-detail", courseId] });
      qc.invalidateQueries({ queryKey: ["admin-courses"] });
      qc.invalidateQueries({ queryKey: ["available-students", courseId] });
      toast.success(`${selectedStudentIds.length} student(s) enrolled successfully`);
      setSelectedStudentIds([]);
      onClose();
    },
    onError: (error: Error) => {
      toast.error(`Failed to assign students: ${error.message}`);
    },
  });

  const availableStudents = students.filter((t) => !enrolledStudentIds.includes(t.id));

  const toggleStudent = (studentId: string) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId) ? prev.filter((id) => id !== studentId) : [...prev, studentId],
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Assign Students to Course</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="border rounded-lg p-4 max-h-96 overflow-y-auto space-y-2">
            {availableStudents.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No students available to assign (all active students are already enrolled)
              </p>
            ) : (
              availableStudents.map((student) => (
                <div
                  key={student.id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent/50 cursor-pointer"
                  onClick={() => toggleStudent(student.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedStudentIds.includes(student.id)}
                    onChange={() => toggleStudent(student.id)}
                  />
                  <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-primary-foreground"
                    style={{ background: "var(--gradient-brand)" }}
                  >
                    {(student.full_name ?? student.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{student.full_name || "No name"}</p>
                    <p className="text-xs text-muted-foreground">{student.email}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {selectedStudentIds.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {selectedStudentIds.length} student{selectedStudentIds.length !== 1 ? "s" : ""}{" "}
              selected
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => assignStudents.mutate()}
            disabled={selectedStudentIds.length === 0 || assignStudents.isPending}
          >
            {assignStudents.isPending
              ? "Assigning..."
              : `Assign ${selectedStudentIds.length} Student${selectedStudentIds.length !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
